import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Button } from '../../../../../../ui';

interface WeightingItem {
  label: string;
  value: number;
  percent: number;
}

const initialItems: WeightingItem[] = [
  { label: 'Location', value: 15, percent: 0 },
  { label: 'Minimum Work Experience', value: 20, percent: 0 },
  { label: 'Mandatory Skills', value: 25, percent: 0 },
  { label: 'Desirable Skills', value: 15, percent: 0 },
  { label: 'Behavioral Attributes', value: 25, percent: 0 },
];

const defaultWeight = parseFloat((100 / initialItems.length).toFixed(2));

interface AIScoreWeightingModel {
  aiScoreWeights?: {
    location?: number;
    minimumWorkExperience?: number;
    mandatorySkills?: number;
    desirableSkills?: number;
    keyword?: number;
    behavioralAttributes?: number;
  };
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    weights: {
      location: number;
      minimumWorkExperience: number;
      mandatorySkills: number;
      desirableSkills: number;
      behavioralAttributes: number;
    } | null
  ) => void;
}

export const AIScoreWeightingModal: React.FC<AIScoreWeightingModel> = ({
  isOpen,
  onClose,
  onSave,
  aiScoreWeights,
}) => {
  // Equal weighting toggle, default disabled
  const [equalWeighting, setEqualWeighting] = useState<boolean>(false);
  // Custom weighting enabled/disabled (default enabled)
  const [weightingEnabled, setWeightingEnabled] = useState<boolean>(true);
  const [items, setItems] = useState<WeightingItem[]>(() => {
    if (aiScoreWeights) {
      const total =
        (aiScoreWeights.location ?? 15) +
        (aiScoreWeights.minimumWorkExperience ?? 20) +
        (aiScoreWeights.mandatorySkills ?? 25) +
        (aiScoreWeights.desirableSkills ?? 15) +
        (aiScoreWeights.behavioralAttributes ?? 25);

      return [
        {
          label: 'Location',
          value: aiScoreWeights.location ?? 15,
          percent:
            total === 0
              ? 0
              : parseFloat(
                  (((aiScoreWeights.location ?? 15) / total) * 100).toFixed(1)
                ),
        },
        {
          label: 'Minimum Work Experience',
          value: aiScoreWeights.minimumWorkExperience ?? 20,
          percent:
            total === 0
              ? 0
              : parseFloat(
                  (
                    ((aiScoreWeights.minimumWorkExperience ?? 20) / total) *
                    100
                  ).toFixed(1)
                ),
        },
        {
          label: 'Mandatory Skills',
          value: aiScoreWeights.mandatorySkills ?? 25,
          percent:
            total === 0
              ? 0
              : parseFloat(
                  (
                    ((aiScoreWeights.mandatorySkills ?? 25) / total) *
                    100
                  ).toFixed(1)
                ),
        },
        {
          label: 'Desirable Skills',
          value: aiScoreWeights.desirableSkills ?? 15,
          percent:
            total === 0
              ? 0
              : parseFloat(
                  (
                    ((aiScoreWeights.desirableSkills ?? 15) / total) *
                    100
                  ).toFixed(1)
                ),
        },
        {
          label: 'Behavioral Attributes',
          value: aiScoreWeights.behavioralAttributes ?? 25,
          percent:
            total === 0
              ? 0
              : parseFloat(
                  (
                    ((aiScoreWeights.behavioralAttributes ?? 25) / total) *
                    100
                  ).toFixed(1)
                ),
        },
      ];
    }
    // Default: custom weighting with calculated percentages
    const total = initialItems.reduce((sum, item) => sum + item.value, 0);
    return initialItems.map((item) => ({
      ...item,
      percent:
        total === 0 ? 0 : parseFloat(((item.value / total) * 100).toFixed(1)),
    }));
  });

  // Calculate percentages whenever values change
  const calculatePercentages = (itemsList: WeightingItem[]) => {
    const total = itemsList.reduce((sum, item) => sum + item.value, 0);
    return itemsList.map((item) => ({
      ...item,
      percent:
        total === 0 ? 0 : parseFloat(((item.value / total) * 100).toFixed(1)),
    }));
  };

  useEffect(() => {
    if (equalWeighting) {
      const equalItems = initialItems.map((item) => ({
        ...item,
        value: defaultWeight,
      }));
      setItems(calculatePercentages(equalItems));
    } else {
      // Restore custom defaults with percentages
      const customItems = initialItems.map((item) => ({ ...item }));
      setItems(calculatePercentages(customItems));
    }
  }, [equalWeighting]);

  const handleValueChange = (index: number, newValue: number) => {
    const updated = [...items];
    updated[index].value = newValue;
    setItems(calculatePercentages(updated));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.dark' }}>
            CV Score Weighting
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="success"
                  checked={equalWeighting}
                  onChange={() => {
                    setEqualWeighting((prev) => !prev);
                    setWeightingEnabled(false);
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Equal Weighting
                </Typography>
              }
              labelPlacement="start"
              sx={{ m: 0, gap: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="success"
                  checked={weightingEnabled}
                  onChange={() => {
                    setWeightingEnabled((prev) => !prev);
                    setEqualWeighting(false);
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Custom Weighting
                </Typography>
              }
              labelPlacement="start"
              sx={{ m: 0, gap: 1 }}
            />
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ my: 1 }}>
        <Stack spacing={3}>
          {items.map((item, idx) => (
            <Stack
              key={idx}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Typography variant="body2" sx={{ flex: 1, color: 'text.primary', fontWeight: 500 }}>
                {item.label}
              </Typography>
              <TextField
                type="number"
                size="small"
                inputProps={{ min: 0, max: 100, step: 0.1, style: { textAlign: 'center', padding: '6px 8px' } }}
                value={item.value}
                onChange={(e) =>
                  handleValueChange(
                    idx,
                    Math.min(parseFloat(e.target.value) || 0, 100)
                  )
                }
                disabled={equalWeighting ? false : !weightingEnabled}
                sx={{ width: 72 }}
              />
              <Typography variant="body2" sx={{ width: 48, textAlign: 'right', color: 'text.secondary', fontWeight: 600 }}>
                {item.percent}%
              </Typography>
            </Stack>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ pt: 2, px: 2, pb: 1, gap: 1 }}>
        <Button onClick={onClose} variant="secondary" label="Cancel" />
        <Button
          onClick={() => {
            if (equalWeighting) {
              const weights = {
                location: defaultWeight,
                minimumWorkExperience: defaultWeight,
                mandatorySkills: defaultWeight,
                desirableSkills: defaultWeight,
                behavioralAttributes: defaultWeight,
              };
              onSave(weights);
            } else if (weightingEnabled) {
              const weights = {
                location: items[0].value,
                minimumWorkExperience: items[1].value,
                mandatorySkills: items[2].value,
                desirableSkills: items[3].value,
                behavioralAttributes: items[4].value,
              };
              onSave(weights);
            } else {
              onSave({
                location: 15,
                minimumWorkExperience: 20,
                mandatorySkills: 25,
                desirableSkills: 15,
                behavioralAttributes: 25,
              });
            }
            onClose();
          }}
          variant="primary"
          label="Save"
        />
      </DialogActions>
    </Dialog>
  );
};

export default AIScoreWeightingModal;
